import { where } from 'firebase/firestore'
import { firestoreService } from './firebase'

export class POSService {
  
  async processSale(posData) {
    try {
      // Create POS transaction record
      const transactionId = await firestoreService.create('pos_transactions', {
        ...posData,
        status: 'completed',
        createdAt: new Date()
      })

      // Generate invoice ID
      const invoiceId = `INV-${Date.now()}-${posData.customerId.slice(-4)}`
      
      // Create invoice data
      const invoiceData = {
        id: invoiceId,
        transactionId,
        customerId: posData.customerId,
        customerName: posData.customerName,
        branchId: posData.branchId,
        items: [
          {
            description: posData.finalService.name,
            quantity: 1,
            unitPrice: posData.finalService.price,
            total: posData.finalService.price,
            type: 'service'
          },
          ...posData.additionalItems.map(item => ({
            description: item.name,
            quantity: item.quantity,
            unitPrice: item.price,
            total: item.price * item.quantity,
            type: 'product'
          }))
        ],
        subtotal: posData.subtotal,
        discount: posData.discount,
        total: posData.total,
        paymentMethod: posData.paymentMethod,
        issueDate: new Date(),
        status: 'paid',
        createdBy: posData.staffId,
        createdAt: new Date()
      }

      // Store invoice in Firestore
      await firestoreService.create('invoices', invoiceData)

      // Update customer loyalty points
      await this.updateCustomerLoyalty(posData.customerId, 0, posData.total)

      // Log upsell if applicable
      if (posData.upsell) {
        await this.logUpsell(posData.upsell, transactionId)
      }

      return { transactionId, invoiceId }

    } catch (error) {
      console.error('Error processing sale:', error)
      throw new Error('Failed to process sale')
    }
  }

  async updateCustomerLoyalty(customerId, pointsEarned, amountSpent) {
    try {
      const customer = await firestoreService.getById('customers', customerId)
      if (customer) {
        const newTotalSpent = (customer.totalSpent || 0) + amountSpent
        const newTotalWashes = (customer.totalWashes || 0) + 1

        await firestoreService.update('customers', customerId, {
          totalSpent: newTotalSpent,
          totalWashes: newTotalWashes,
          lastVisit: new Date()
        })
      }
    } catch (error) {
      console.error('Error updating customer loyalty:', error)
    }
  }

  async logUpsell(upsellData, transactionId) {
    try {
      await firestoreService.create('upsells', {
        ...upsellData,
        transactionId,
        createdAt: new Date()
      })
    } catch (error) {
      console.error('Error logging upsell:', error)
    }
  }

  async getTransactionsByBranch(branchId, startDate, endDate) {
    const constraints = [
      ['branchId', '==', branchId],
      ['createdAt', '>=', startDate],
      ['createdAt', '<=', endDate]
    ]
    return firestoreService.getAll('pos_transactions', constraints)
  }

  async getInvoices(branchId = null) {
    const constraints =
      branchId && branchId !== 'all-branches' ? [where('branchId', '==', branchId)] : []
    return firestoreService.getAll('invoices', constraints)
  }

  async getCustomers() {
    // Fetch all customers
    return firestoreService.getAll('customers')
  }

  async getTransactions(branchId = null) {
    const constraints =
      branchId && branchId !== 'all-branches' ? [where('branchId', '==', branchId)] : []
    return firestoreService.getAll('pos_transactions', constraints)
  }

  /**
   * Owner-only correction of an invoice (and linked POS transaction when present).
   */
  async updateInvoice(invoice, form, editor) {
    const docId = invoice?.firestoreId || invoice?.id
    if (!docId) throw new Error('Invoice id missing')

    const items = (form.items || []).map((item) => {
      const quantity = Number(item.quantity) || 0
      const unitPrice = Number(item.unitPrice ?? item.price) || 0
      return {
        ...item,
        name: item.name || item.description || 'Item',
        description: item.description || item.name || 'Item',
        quantity,
        unitPrice,
        price: unitPrice,
        total: quantity * unitPrice,
      }
    })

    const subtotal = items.reduce((sum, it) => sum + Number(it.total || 0), 0)
    const discountAmount = Math.max(0, Number(form.discountAmount) || 0)
    const total = Math.max(0, subtotal - discountAmount)

    const existingDiscount = invoice.discount
    const discountPayload =
      existingDiscount && typeof existingDiscount === 'object'
        ? {
            ...existingDiscount,
            amount: discountAmount,
            reason: form.editReason || existingDiscount.reason || 'Owner correction',
          }
        : discountAmount

    const patch = {
      customerName: String(form.customerName || '').trim(),
      customerPhone: String(form.customerPhone || '').trim(),
      customerEmail: String(form.customerEmail || '').trim(),
      paymentMethod: form.paymentMethod || invoice.paymentMethod || 'cash',
      status: form.status || invoice.status || 'paid',
      items,
      subtotal,
      discount: discountPayload,
      total,
      editedAt: new Date(),
      editedBy: editor?.uid || null,
      editedByName: editor?.name || editor?.email || null,
      editReason: String(form.editReason || '').trim() || 'Owner correction',
    }

    await firestoreService.update('invoices', docId, patch)

    // Keep linked POS transaction in sync when we have a transactionId
    const txId = invoice.transactionId
    if (txId) {
      try {
        await firestoreService.update('pos_transactions', txId, {
          customerName: patch.customerName,
          customerPhone: patch.customerPhone,
          paymentMethod: patch.paymentMethod,
          items: items.map((it) => ({
            name: it.name,
            quantity: it.quantity,
            price: it.unitPrice,
            category: it.category || it.type || 'service',
          })),
          subtotal,
          discountAmount,
          total,
          editedAt: new Date(),
          editedBy: patch.editedBy,
          editReason: patch.editReason,
        })
      } catch (e) {
        console.warn('Invoice updated; linked POS transaction sync skipped:', e)
      }
    }

    return { ...invoice, ...patch, firestoreId: docId, id: docId }
  }

  async processTransaction(transactionData) {
    try {
      // Create POS transaction record
      const transactionId = await firestoreService.create('pos_transactions', {
        ...transactionData,
        status: 'completed',
        createdAt: new Date()
      })

      // Generate invoice ID
      const invoiceId = `INV-${Date.now()}-${transactionData.customerId?.slice(-4) || 'WALK'}`
      
      // Create invoice data
      const invoiceData = {
        id: invoiceId,
        transactionId,
        customerId: transactionData.customerId,
        customerName: transactionData.customerName,
        branchId: transactionData.branchId,
        items: transactionData.items.map(item => ({
          description: item.name,
          quantity: item.quantity,
          unitPrice: item.price,
          total: item.price * item.quantity,
          type: item.category
        })),
        subtotal: transactionData.subtotal,
        discount: transactionData.discountAmount || 0,
        total: transactionData.total,
        paymentMethod: transactionData.paymentMethod,
        issueDate: new Date(),
        status: 'paid',
        createdBy: transactionData.staffId,
        createdAt: new Date()
      }

      // Store invoice in Firestore
      await firestoreService.create('invoices', invoiceData)

      // Track spend/visits. Voucher progress is applied once in InvoiceGenerator.
      if (transactionData.customerId && transactionData.customerId !== 'walk-in') {
        await this.updateCustomerLoyalty(transactionData.customerId, 0, transactionData.total)
      }

      return { 
        id: transactionId, 
        invoiceId,
        ...transactionData 
      }

    } catch (error) {
      console.error('Error processing transaction:', error)
      throw new Error('Failed to process transaction')
    }
  }
}

export const posService = new POSService() 